import { useTranslation } from 'react-i18next';
import { TextInput, NumberInput, Select } from '@mantine/core';
import { MoneyNumberInput } from '@/components/snippets';
import DatePickerCustom from '@/components/custom-date-picker';
import dayjs from 'dayjs';
import { localTenant } from '@/services/session-service';

interface Props {
    receiverName: string;
    setReceiverName: (val: string) => void;
    docTipo: string;
    setDocTipo: (val: string) => void;
    docNro: string;
    setDocNro: (val: string) => void;
    invoiceType: string;
    setInvoiceType: (val: string) => void;
    pos: number | null;
    setPos: (val: number | null) => void;
    concepto: string;
    setConcepto: (val: string) => void;
    description?: string;
    setDescription?: (val: string) => void;
    ivaRate: string;
    setIvaRate: (val: string) => void;
    netAmount: string | number;
    ivaAmount: number;
    totalAmount: number;
    setTotalAmount?: (val: number) => void;
    fchServDesde: Date | null;
    setFchServDesde: (val: Date | null) => void;
    fchServHasta: Date | null;
    setFchServHasta: (val: Date | null) => void;
    fchVtoPago: Date | null;
    setFchVtoPago: (val: Date | null) => void;
    taxCondition: string;
    setTaxCondition: (val: string) => void;
    disabled?: boolean;
    isNameDocEditable?: boolean;
    isTotalAmountEditable?: boolean;
}

export default function AfipInvoiceFields({
    receiverName,
    setReceiverName,
    docTipo,
    setDocTipo,
    docNro,
    setDocNro,
    invoiceType,
    setInvoiceType,
    pos,
    setPos,
    concepto,
    setConcepto,
    description = '',
    setDescription,
    ivaRate,
    setIvaRate,
    netAmount,
    ivaAmount,
    totalAmount,
    setTotalAmount,
    fchServDesde,
    setFchServDesde,
    fchServHasta,
    setFchServHasta,
    fchVtoPago,
    setFchVtoPago,
    taxCondition,
    setTaxCondition,
    disabled = false,
    isNameDocEditable = true,
    isTotalAmountEditable = false
}: Props) {
    const { t } = useTranslation();
    const tenant = localTenant();

    const afipSettings: any = tenant?.settings?.afip;
    const rawSalesPoints: any[] = Array.isArray(afipSettings?.sales_points) && afipSettings.sales_points.length > 0
        ? afipSettings.sales_points
        : Array.isArray(afipSettings?.all_sales_points) && afipSettings.all_sales_points.length > 0
            ? afipSettings.all_sales_points
            : (afipSettings?.pos_number ? [{ number: afipSettings.pos_number, name: afipSettings.pos_name || 'Turizi POS', system: afipSettings.pos_system || '' }] : []);

    let posSelectData = rawSalesPoints.map((p: any) => {
        const numStr = String(p.number ?? p.numero ?? p.Nro ?? p.id ?? '').trim();
        const nameStr = String(p.name || p.displayName || p.nombreFantasia || p.Nombre || '').trim();
        const sysStr = String(p.system || p.sistema || p.EmisionTipo || p.Sistema || '').trim();
        const label = `POS ${numStr}${nameStr ? ` - ${nameStr}` : ''}${sysStr ? ` (${sysStr})` : ''}`;
        return { value: numStr, label };
    }).filter((item) => item.value !== '' && item.value !== 'undefined' && item.value !== 'null');

    if (pos != null && !posSelectData.some(p => p.value === String(pos))) {
        posSelectData.unshift({
            value: String(pos),
            label: `POS ${pos}`
        });
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <TextInput
                    label={t('name')}
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    required
                    disabled={disabled || !isNameDocEditable}
                />
                <Select
                    label={t('type')}
                    value={docTipo}
                    onChange={(value) => setDocTipo(value || '99')}
                    data={[
                        { value: '80', label: 'CUIT' },
                        { value: '86', label: 'CUIL' },
                        { value: '96', label: 'DNI' },
                        { value: '94', label: 'Pasaporte' },
                        { value: '99', label: 'Consumidor Final / Sin Identificar' }
                    ]}
                    required
                    disabled={disabled || !isNameDocEditable}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <TextInput
                    label={t('number')}
                    value={docNro}
                    onChange={(e) => setDocNro(e.target.value)}
                    disabled={disabled || !isNameDocEditable || docTipo === '99'}
                    required={docTipo !== '99'}
                />
                <Select
                    label={t('tax_condition')}
                    value={taxCondition}
                    onChange={(value) => setTaxCondition(value || '5')}
                    data={[
                        { value: '1', label: 'IVA Responsable Inscripto' },
                        { value: '4', label: 'IVA Sujeto Exento' },
                        { value: '5', label: 'Consumidor Final' },
                        { value: '6', label: 'Responsable Monotributo' },
                        { value: '8', label: 'Sujeto No Categorizado' },
                        { value: '9', label: 'Proveedor del Exterior' },
                        { value: '10', label: 'Cliente del Exterior' },
                        { value: '11', label: 'IVA Liberado - Ley Nº 19.640' },
                        { value: '13', label: 'Monotributista Social' },
                        { value: '15', label: 'IVA No Alcanzado' },
                        { value: '17', label: 'Monotributista Trabajador Independiente Promovido' }
                    ]}
                    required={docTipo !== '99'}
                    disabled={disabled || !isNameDocEditable || docTipo === '99'}
                />
                <Select
                    label={t('invoice_type')}
                    value={invoiceType}
                    onChange={(value) => setInvoiceType(value || '6')}
                    data={[
                        { value: '1', label: 'Factura A' },
                        { value: '6', label: 'Factura B' },
                        { value: '11', label: 'Factura C' },
                        { value: '195', label: 'Factura T' }
                    ]}
                    required
                    disabled={disabled}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Select
                    label={t('pos_label')}
                    value={pos ? String(pos) : null}
                    onChange={(value) => setPos(value ? parseInt(value, 10) : null)}
                    data={posSelectData}
                    placeholder={t('select_pos_placeholder')}
                    searchable
                    clearable
                    required
                    disabled={disabled}
                />
                <Select
                    label={t('concept_label')}
                    value={concepto}
                    onChange={(value) => setConcepto(value || '2')}
                    data={[
                        { value: '1', label: 'Productos' },
                        { value: '2', label: 'Servicios' },
                        { value: '3', label: 'Ambos' }
                    ]}
                    required
                    disabled={disabled}
                />
            </div>
            {setDescription && (
                <TextInput
                    label={t('invoice_description_label')}
                    placeholder={t('invoice_description_placeholder')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={disabled}
                />
            )}
            {(concepto === '2' || concepto === '3') && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <DatePickerCustom
                        label={t('service_period_start')}
                        value={fchServDesde ? dayjs(fchServDesde).format('YYYY-MM-DD') : ''}
                        onChange={(val) => setFchServDesde(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                        valueFormat="DD MMM YYYY"
                        clearable={false}
                        disabled={disabled}
                    />
                    <DatePickerCustom
                        label={t('service_period_end')}
                        value={fchServHasta ? dayjs(fchServHasta).format('YYYY-MM-DD') : ''}
                        onChange={(val) => setFchServHasta(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                        valueFormat="DD MMM YYYY"
                        clearable={false}
                        disabled={disabled}
                    />
                    <DatePickerCustom
                        label={t('due_date')}
                        value={fchVtoPago ? dayjs(fchVtoPago).format('YYYY-MM-DD') : ''}
                        onChange={(val) => setFchVtoPago(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                        valueFormat="DD MMM YYYY"
                        clearable={false}
                        disabled={disabled}
                    />
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {invoiceType !== '11' ? (
                    <Select
                        label={t('iva_rate')}
                        value={ivaRate}
                        onChange={(value) => setIvaRate(value || '5')}
                        data={[
                            { value: '5', label: '21%' },
                            { value: '4', label: '10.5%' },
                            { value: '6', label: '27%' },
                            { value: '3', label: '0%' }
                        ]}
                        required
                        disabled={disabled}
                    />
                ) : (
                    <TextInput
                        label={t('iva_rate')}
                        value="Exento (Factura C)"
                        disabled
                    />
                )}
                <TextInput
                    label={t('tax_net_amount')}
                    value={typeof netAmount === 'number' ? netAmount.toFixed(2) : '0.00'}
                    disabled
                />
                <TextInput
                    label={t('iva_amount')}
                    value={ivaAmount.toFixed(2)}
                    disabled
                />
                {isTotalAmountEditable && setTotalAmount ? (
                    <MoneyNumberInput
                        label={t('total_amount')}
                        amount={totalAmount}
                        onChange={(val) => setTotalAmount(val || 0)}
                        disabled={disabled}
                    />
                ) : (
                    <TextInput
                        label={t('total_amount')}
                        value={totalAmount.toFixed(2)}
                        disabled
                    />
                )}
            </div>
        </div>
    );
}
